import { useState } from 'react';
import { SlidersHorizontal, X, Filter, ChevronDown } from 'lucide-react';
import '../accessoriesStyles/filter-bar.css';

export function FilterBar({ filters, onFilterChange, availableCategories, availableBrands }) {
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const toggleCategory = (category) => {
    const newCategories = filters.categories.includes(category)
      ? filters.categories.filter(c => c !== category)
      : [...filters.categories, category];
    onFilterChange({ ...filters, categories: newCategories });
  };

  const toggleBrand = (brand) => {
    const newBrands = filters.brands.includes(brand)
      ? filters.brands.filter(b => b !== brand)
      : [...filters.brands, brand];
    onFilterChange({ ...filters, brands: newBrands });
  };

  const setSortBy = (sort) => {
    onFilterChange({ ...filters, sortBy: sort });
  };

  const clearAllFilters = () => {
    onFilterChange({
      categories: [],
      brands: [],
      priceRange: [0, 120000],
      sortBy: 'none'
    });
  };

  const hasActiveFilters = filters.categories.length > 0 || filters.brands.length > 0 || filters.sortBy !== 'none';

  const getActiveFilterCount = () => {
    let count = filters.categories.length + filters.brands.length;
    if (filters.sortBy !== 'none') {
      count += 1;
    }
    return count;
  };

  const sortOptions = [
    { label: 'Default', value: 'none' },
    { label: 'Price: Low to High', value: 'lowToHigh' },
    { label: 'Price: High to Low', value: 'highToLow' },
  ];

  // Mobile Filter Content Component
  const FilterContent = () => (
    <div className="mobile-filter-content">
      {/* Clear All Button */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="mobile-clear-filters-button"
        >
          <X />
          Clear All Filters ({getActiveFilterCount()})
        </button>
      )}

      {/* Category Section */}
      <div className="filter-content-section">
        <h3 className="filter-content-title">
          Category
          {filters.categories.length > 0 && (
            <span className="filter-content-badge">
              {filters.categories.length}
            </span>
          )}
        </h3>
        <div className="filter-content-buttons">
          {availableCategories.map((category) => (
            <button
              key={category}
              onClick={() => toggleCategory(category)}
              className={`filter-content-button ${filters.categories.includes(category) ? 'active' : ''}`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Brand Section */}
      <div className="filter-content-section">
        <h3 className="filter-content-title">
          Brand
          {filters.brands.length > 0 && (
            <span className="filter-content-badge">
              {filters.brands.length}
            </span>
          )}
        </h3>
        <div className="filter-content-buttons">
          {availableBrands.map((brand) => (
            <button
              key={brand}
              onClick={() => toggleBrand(brand)}
              className={`filter-content-button ${filters.brands.includes(brand) ? 'active' : ''}`}
            >
              {brand}
            </button>
          ))}
        </div>
      </div>

      {/* Sort By Section */}
      <div className="filter-content-section">
        <h3 className="filter-content-title">Sort By</h3>
        <div className="filter-sort-buttons">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`filter-sort-button ${filters.sortBy === option.value ? 'active' : ''}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="filter-bar">
      <div className="filter-bar-container">
        {/* Mobile & Tablet Filter Button - Visible on small and medium screens */}
        <div className="mobile-filter-container">
          <button 
            className="mobile-filter-button"
            onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
          >
            <Filter />
            Filters & Sort
            {hasActiveFilters && (
              <span className="filter-badge">
                {getActiveFilterCount()}
              </span>
            )}
            <ChevronDown className={`chevron ${mobileFilterOpen ? 'rotated' : ''}`} />
          </button>
          
          {mobileFilterOpen && (
            <div className="mobile-filter-dropdown">
              <div className="mobile-filter-header">
                <h3>Filters & Sort</h3>
                <button 
                  className="close-button"
                  onClick={() => setMobileFilterOpen(false)}
                >
                  <X />
                </button>
              </div>
              <FilterContent />
            </div>
          )}
        </div>

        {/* Desktop Filter Bar - Visible on large screens and up */}
        <div className="desktop-filter-bar">
          {/* Filter Icon & Title */}
          <div className="filter-icon-group">
            <div className="filter-icon-box">
              <SlidersHorizontal />
            </div>
            <span className="filter-label">Filters:</span>
          </div>

          {/* Category Filters */}
          <div className="filter-section">
            <span className="filter-section-label">Category</span>
            <div className="filter-buttons-group">
              {availableCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => toggleCategory(category)}
                  className={`filter-button ${filters.categories.includes(category) ? 'active' : ''}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Separator */}
          <div className="filter-separator" />

          {/* Brand Filters */}
          <div className="filter-section">
            <span className="filter-section-label">Brand</span>
            <div className="filter-buttons-group">
              {availableBrands.map((brand) => (
                <button
                  key={brand}
                  onClick={() => toggleBrand(brand)}
                  className={`filter-button ${filters.brands.includes(brand) ? 'active' : ''}`}
                >
                  {brand}
                </button>
              ))}
            </div>
          </div>

          {/* Separator */}
          <div className="filter-separator" />

          {/* Sort By Filters */}
          <div className="filter-section">
            <span className="filter-section-label">Sort By</span>
            <div className="filter-buttons-group">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSortBy(option.value)}
                  className={`filter-button ${filters.sortBy === option.value ? 'active' : ''}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Clear All Button */}
          {hasActiveFilters && (
            <>
              <div className="filter-separator" style={{ marginLeft: 'auto' }} />
              <button
                onClick={clearAllFilters}
                className="clear-filters-button"
              >
                <X />
                Clear ({getActiveFilterCount()})
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
